import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { contentStringApi, contentStringKeys } from '@/api/skin/contentStringApi';
import { type ContentStringCategory } from '@/models/skin/ContentString';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import { useToast } from '../../common/toast/ToastProvider';

interface Props {
    open: boolean;
    categories: ContentStringCategory[];
    onClose: () => void;
}

/**
 * Categories are labels, so this stays a small in-place list rather than a screen of its own - renaming one
 * is the common act, and adding one happens when a new area of the product gets its first string.
 */
const ContentStringCategoryDialog = ({ open, categories, onClose }: Props) => {
    const strings = useLocalizedStrings();
    const toast = useToast();
    const qc = useQueryClient();

    const [names, setNames] = useState<Record<string, string>>(
        () => Object.fromEntries(categories.map(c => [c.id, c.name])),
    );
    const [newName, setNewName] = useState('');

    const refresh = async () => {
        await qc.invalidateQueries({ queryKey: contentStringKeys.categories() });
        // Keys carry their category name, so a rename leaves every row in the catalogue stale.
        await qc.invalidateQueries({ queryKey: contentStringKeys.keys() });
    };

    const onError = (e: unknown, fallback: string) => {
        const body = (e as { body?: string })?.body;
        toast({ severity: 'error', message: typeof body === 'string' && body ? body : fallback });
    };

    const { mutateAsync: create, isPending: creating } = useMutation({
        mutationFn: (name: string) => contentStringApi.createCategory(name),
        onSuccess: async () => { setNewName(''); await refresh(); },
        onError: e => onError(e, strings.contentStringCategorySaveError),
    });

    const { mutateAsync: rename } = useMutation({
        mutationFn: ({ id, name }: { id: string; name: string }) => contentStringApi.updateCategory(id, name),
        onSuccess: async () => { toast({ severity: 'success', message: strings.contentStringCategorySaved }); await refresh(); },
        onError: e => onError(e, strings.contentStringCategorySaveError),
    });

    const { mutateAsync: remove } = useMutation({
        mutationFn: (id: string) => contentStringApi.deleteCategory(id),
        onSuccess: async () => { toast({ severity: 'success', message: strings.contentStringCategoryDeleted }); await refresh(); },
        // A 409 here means the category still holds keys, and the server names the count. Showing its wording
        // beats a generic failure that sends someone looking for a cause we already know.
        onError: e => onError(e, strings.contentStringCategoryDeleteError),
    });

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>{strings.contentStringManageCategories}</DialogTitle>

            <DialogContent>
                <Typography variant="caption" color="text.secondary">
                    {strings.contentStringCategoriesHelp}
                </Typography>

                <List dense>
                    {categories.map(c => (
                        <ListItem key={c.id} disableGutters sx={{ gap: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                value={names[c.id] ?? ''}
                                onChange={e => setNames(n => ({ ...n, [c.id]: e.target.value }))}
                                onBlur={() => {
                                    const next = (names[c.id] ?? '').trim();
                                    if (next && next !== c.name) rename({ id: c.id, name: next });
                                }}
                            />
                            <Tooltip title={strings.remove}>
                                <IconButton onClick={() => remove(c.id)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </ListItem>
                    ))}
                </List>

                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label={strings.contentStringNewCategory}
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && newName.trim()) { e.preventDefault(); create(newName.trim()); } }}
                    />
                    <Button
                        startIcon={<AddIcon />}
                        disabled={!newName.trim() || creating}
                        loading={creating}
                        onClick={() => create(newName.trim())}
                    >
                        {strings.add}
                    </Button>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{strings.close}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ContentStringCategoryDialog;
